/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { Box, Button, Grid, Typography, CircularProgress } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { resetTempUserInfo } from '@/features/habitation/userSlice';
import { reset } from '../rcSlice';
import { attestationRcContract } from '@/api/rcApi';
import { appError, appLoading, appSuccess } from '@/redux/actions/appActions';
import { authSuccess } from '@/redux/actions/authActions';
import { getDocument } from '@/redux/actions/contractsActions';
import { getMe, createEmailConfirmationStream } from '@/api/authApi';
import LocalStorageService from '@/services/LocalStorageService';
import type { EmailConfirmationMessage } from '@/features/habitation/types';

const Marge = styled.div`
  height: 150px;
`;

const Spacing = styled.div`
  height: 80px;
`;

const ButtonStyled = styled(Button)`
  background-color: #2f6975;
  color: white;
  border-radius: 30px;
  width: 200px;
  height: 40px;
  font-weight: 400;
  font-size: 12px;
  &:hover {
    background-color: #26555f;
  }
  @media (max-width: 600px) {
    width: 100%;
    font-size: 13px;
    height: 30px;
  }
`;

const RcSuccessPage: React.FC = () => {
  const { t } = useTranslation('rc');
  const dispatch = useAppDispatch();
  const isTempRegistered = useAppSelector(state => state.user.isTempRegistered);

  // Lire contractReference depuis l'URL (retour SellAndSign) ou fallback Redux/localStorage
  const contractReference = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('contractReference');
  }, []);

  const [attestationId, setAttestationId] = useState<number | null>(null);
  const [checkingValidation, setCheckingValidation] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const [attestationError, setAttestationError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const eventSourceRef = useRef<EventSource | null>(null);
  const isMountedRef = useRef(true);
  const hasCalledAttestationRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!contractReference) return;

    if (!isTempRegistered && hasCalledAttestationRef.current) return;

    dispatch(appLoading());

    if (!isTempRegistered) {
      hasCalledAttestationRef.current = true;
      setAttestationError(false);
      attestationRcContract(contractReference)
        .then(res => {
          if (!isMountedRef.current) return;
          dispatch(appSuccess());
          if (res.data?.attestationId) {
            setAttestationId(res.data.attestationId);
            setAttestationError(false);
          } else {
            setAttestationError(true);
          }
          localStorage.removeItem('rc_contractReference');
          dispatch(reset());
          dispatch(resetTempUserInfo());
        })
        .catch(err => {
          if (!isMountedRef.current) return;
          dispatch(appError());
          setAttestationError(true);
          console.error('Erreur récupération attestation RC:', err);
        });
      return;
    }

    dispatch(reset());
    dispatch(appSuccess());
    setCheckingValidation(true);

    if (!contractReference) return;

    eventSourceRef.current = createEmailConfirmationStream(contractReference, {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onOpen: () => {},
      onMessage: (data: EmailConfirmationMessage) => {
        if (!isMountedRef.current) return;
        if (data.status === 'confirmed' && data.active && data.isVerified) {
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }

          localStorage.removeItem('accountValidated');
          dispatch(resetTempUserInfo());

          getMe()
            .then(response => {
              if (!isMountedRef.current) return;
              if (response?.data) {
                dispatch(authSuccess({ data: { user: response.data }, token: null }));
                // Sync localStorage for isAuthenticated() and global app state
                const service = LocalStorageService.getService();
                service.setUser({ user: response.data });
              }
            })
            .catch(err => console.error('Erreur mise à jour auth:', err));

          setCheckingValidation(false);
          setEmailValidated(true);

          attestationRcContract(contractReference)
            .then(res => {
              if (!isMountedRef.current) return;
              if (res.data?.attestationId) {
                setAttestationId(res.data.attestationId);
              }
              localStorage.removeItem('rc_contractReference');
              dispatch(reset());
            })
            .catch(err => {
              console.error('Erreur récupération attestation:', err);
              dispatch(appError());
            });
        }
      },
      onError: () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        setTimeout(async () => {
          if (!isMountedRef.current) return;
          try {
            const res = await getMe();
            if (res.status === 200 && res.data?.active && res.data?.isVerified) {
              localStorage.removeItem('accountValidated');
              dispatch(resetTempUserInfo());
              window.location.href = window.location.pathname + window.location.search;
            }
          } catch (err) {
            console.error('Erreur check manuel:', err);
          }
        }, 2000);
      },
    });
  }, [contractReference, isTempRegistered, dispatch, retryCount]);

  const handleRetry = () => {
    hasCalledAttestationRef.current = false;
    setAttestationError(false);
    setRetryCount(c => c + 1);
  };

  const handleClick = (id: number) => {
    dispatch(getDocument({ id, filename: 'attestation-rc.pdf' }));
  };

  return (
    <>
      <Marge />
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 2, mt: 2, position: 'relative' }}>
        {(!isTempRegistered || emailValidated) && (
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => (window.location.href = '/home')}
              sx={{
                borderColor: '#2f6975',
                color: '#2f6975',
                '&:hover': { borderColor: '#26555f', backgroundColor: 'rgba(47, 105, 117, 0.04)' },
              }}
            >
              {t('stepfinal.back_to_home')}
            </Button>
          </Box>
        )}

        <Grid container>
          <Grid item xs={12} textAlign="center">
            <Box
              component="img"
              src="/images/logos/logo-blue.png"
              alt="logo"
              sx={{ height: { xs: 64, sm: 90 }, width: { xs: 42, sm: 52 }, mx: 'auto' }}
            />
          </Grid>
          <Spacing />
          <Grid item xs={12} textAlign="center">
            <Typography variant="h3">{t('stepfinal.subscribe_success')}</Typography>
          </Grid>

          {(!isTempRegistered || emailValidated) && (
            <>
              <Spacing />
              <Grid item xs={12} textAlign="center">
                <Typography variant="h1">{t('stepfinal.subscribe_thanks')}</Typography>
              </Grid>
              <Spacing />
            </>
          )}

          {isTempRegistered && checkingValidation && !emailValidated && (
            <Grid item xs={12} textAlign="center" mt={4}>
              <CircularProgress color="primary" />
              <Box mt={2}>
                <Typography variant="h5">{t('stepfinal.waiting_validation')}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('stepfinal.email_sent_check')}
                </Typography>
              </Box>
            </Grid>
          )}

          {(!isTempRegistered || emailValidated) && attestationId && (
            <Grid item xs={12} textAlign="center" mt={2}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  background: '#e3f2fd',
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                  mt: 2,
                  boxShadow: 1,
                }}
              >
                <InsertDriveFileIcon color="primary" sx={{ fontSize: 32 }} />
                <ButtonStyled
                  type="button"
                  onClick={() => handleClick(attestationId)}
                  style={{
                    fontWeight: 600,
                    color: '#1976d2',
                    textDecoration: 'none',
                    fontSize: 12,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  {t('stepfinal.download_attestation')}
                </ButtonStyled>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, color: 'gray' }}>
                {t('stepfinal.download_attestation_hint')}
              </Typography>
            </Grid>
          )}

          {(!isTempRegistered || emailValidated) && !attestationId && attestationError && (
            <Grid item xs={12} textAlign="center" mt={2}>
              <Typography variant="h6" sx={{ color: '#c62828' }}>
                {t('stepfinal.attestation_error_title')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'gray' }}>
                {t('stepfinal.attestation_error_hint')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2, flexWrap: 'wrap' }}>
                <ButtonStyled type="button" onClick={handleRetry}>
                  {t('stepfinal.retry')}
                </ButtonStyled>
                <ButtonStyled type="button" onClick={() => (window.location.href = '/documents')}>
                  {t('stepfinal.go_to_account')}
                </ButtonStyled>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
      <Marge />
    </>
  );
};

export default RcSuccessPage;
